import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import NFTCard from "@/components/NFTCard";
import { Input } from "@/components/ui/input";
import { Search, Filter, Loader2 } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const MARKET_BG = "https://static.prod-images.emergentagent.com/jobs/3314a7a0-c2a5-4cde-8293-8d2f5e2e9aac/images/a8dad2e7639b3c3425c4fa2b237e6f45f931d26fc9d178b9af9dfb96f7220c36.png";

export default function Marketplace() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [contractType, setContractType] = useState("all");
  const [listType, setListType] = useState("all");
  const [creatorRole, setCreatorRole] = useState("all");
  const [sort, setSort] = useState("newest");

  const fetchItems = async () => {
    setLoading(true);
    try {
      const params = {};
      if (q) params.q = q;
      if (contractType !== "all") params.contract_type = contractType;
      if (listType !== "all") params.list_type = listType;
      if (creatorRole !== "all") params.creator_role = creatorRole;
      params.sort = sort;
      const { data } = await api.get("/marketplace", { params });
      setItems(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchItems();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    const t = setTimeout(fetchItems, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [q, contractType, listType, creatorRole, sort]);

  return (
    <div>
      {/* Hero */}
      <div className="relative overflow-hidden border-b border-white/10">
        <div className="absolute inset-0 opacity-40" style={{ backgroundImage: `url(${MARKET_BG})`, backgroundSize: "cover" }} />
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/70 to-[#050505]" />
        <div className="relative max-w-7xl mx-auto px-6 md:px-10 py-20">
          <div className="text-xs uppercase tracking-[0.3em] text-zinc-400">The Market</div>
          <h1 className="font-display text-5xl sm:text-6xl tracking-tighter mt-3">Discover student work</h1>
          <p className="text-zinc-400 mt-3 max-w-xl">
            Browse minted assets from creators across registered schools. Buy, bid, collect.
          </p>
        </div>
      </div>

      {/* Filters */}
      <div className="max-w-7xl mx-auto px-6 md:px-10 py-8">
        <div className="grid md:grid-cols-12 gap-3 items-center">
          <div className="md:col-span-5 relative">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
            <Input
              data-testid="market-search-input"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search by title, creator, or tag…"
              className="pl-10 bg-[#0A0A0A] border-white/10 h-11"
            />
          </div>
          <div className="md:col-span-2">
            <Select value={contractType} onValueChange={setContractType}>
              <SelectTrigger data-testid="filter-contract" className="bg-[#0A0A0A] border-white/10 h-11"><SelectValue placeholder="Contract" /></SelectTrigger>
              <SelectContent className="bg-[#0F0F0F] border-white/10 text-white">
                <SelectItem value="all">All contracts</SelectItem>
                <SelectItem value="ERC-721">ERC-721</SelectItem>
                <SelectItem value="ERC-1155">ERC-1155</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="md:col-span-2">
            <Select value={listType} onValueChange={setListType}>
              <SelectTrigger data-testid="filter-list" className="bg-[#0A0A0A] border-white/10 h-11"><SelectValue placeholder="Listing" /></SelectTrigger>
              <SelectContent className="bg-[#0F0F0F] border-white/10 text-white">
                <SelectItem value="all">All listings</SelectItem>
                <SelectItem value="fixed">Fixed price</SelectItem>
                <SelectItem value="auction">Auction</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="md:col-span-2">
            <Select value={creatorRole} onValueChange={setCreatorRole}>
              <SelectTrigger className="bg-[#0A0A0A] border-white/10 h-11"><SelectValue placeholder="Creator" /></SelectTrigger>
              <SelectContent className="bg-[#0F0F0F] border-white/10 text-white">
                <SelectItem value="all">All creators</SelectItem>
                <SelectItem value="student">Students</SelectItem>
                <SelectItem value="teacher">Teachers (Lessons)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="md:col-span-1">
            <Select value={sort} onValueChange={setSort}>
              <SelectTrigger className="bg-[#0A0A0A] border-white/10 h-11"><Filter size={14} /></SelectTrigger>
              <SelectContent className="bg-[#0F0F0F] border-white/10 text-white">
                <SelectItem value="newest">Newest</SelectItem>
                <SelectItem value="price_low">Price ↑</SelectItem>
                <SelectItem value="price_high">Price ↓</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Grid */}
        <div className="mt-10">
          {loading ? (
            <div className="grid place-items-center py-20 text-zinc-500"><Loader2 className="animate-spin" /></div>
          ) : items.length === 0 ? (
            <div className="border border-dashed border-white/10 rounded-xl py-20 text-center">
              <div className="text-zinc-400">No assets yet — be the first to mint.</div>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {items.map((a) => (
                <NFTCard key={a.id} asset={a} />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
